import * as React from 'react';

import type { FetchResult } from '@apollo/client';

import logger from 'js/app/loggerSingleton';
import { FormattedDate, FormattedMessage } from 'js/lib/coursera.react-intl';

import { useTracker } from '@coursera/event-pulse/react';

import { getCopy } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import type { CancelSubscriptionMutation } from 'bundles/my-purchases-graphql/components/manage-subscriptions/queries/__generated__/CancelSubscriptionMutation';
import RefundErrorModal from 'bundles/my-purchases-graphql/components/modals/RefundErrorModal';
import { isNextBillingCycleDiscounted } from 'bundles/my-purchases-graphql/components/utils/isNextBillingCycleDiscounted';
import { getEventingV3Product } from 'bundles/my-purchases-graphql/lib/eventingUtils';
import getTranslations from 'bundles/my-purchases-graphql/lib/subscriptionCancelMessage';
import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import type SubscriptionNextBillDetailsV1 from 'bundles/naptimejs/resources/subscriptionNextBillDetails.v1';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';
import { useGetSpecializationById } from 'bundles/payments-common/hooks/useGetSpecializationById';
import type { SpecializationType } from 'bundles/payments-common/hooks/useGetSpecializationById';
import { SPECIALIZATION_SUBSCRIPTION } from 'bundles/payments/common/ProductType';
import { currencyIdMap } from 'bundles/payments/lib/currencies';
import type { Props as RefundModalProps } from 'bundles/refund/components/RefundModal';
import RefundModal from 'bundles/refund/components/RefundModal';
import { useCart, useGetSubscriptionNextBillDetails } from 'bundles/refund/utils/hooks';
import { getS12nPaymentsDisplayName } from 'bundles/s12n-common/lib/s12nProductVariantUtils';
import { cancel } from 'bundles/subscriptions-common/lib/subscriptionsV1Client';
import {
  generateImpressionForCancellationUndo,
  isCancellationUndoEnabled,
  isEligibleForStripeCancellation,
  useCancelSubscription,
} from 'bundles/subscriptions-common/utils/cancellationUndoUtils';
import SurveyResponseActionType from 'bundles/survey-form/constants/SurveyResponseActionType';

import _t from 'i18n!nls/refund';

import 'css!./__styles__/CancelSubscriptionModal';

const EVENTING_V3_MODAL_NAME = 'cancellation';

export type InputProps = {
  onClose: (didSuccessfullyCancel?: boolean) => void;
  subscription: SubscriptionsV1;
};

export type Props = InputProps & {
  s12n?: SpecializationType;
  cart?: CartsV2;
  subscriptionNextBillDetails?: SubscriptionNextBillDetailsV1;
  trackDecline: () => void;
  trackConfirm: () => void;
  cancelSubscription?: (subscriptionId: string) => Promise<FetchResult<CancelSubscriptionMutation>>;
};

type State = {
  didSuccessfullyCancel: boolean;
  showErrorModal: boolean;
};

type AdditionalRefundModalProps = Pick<
  RefundModalProps,
  'closeButtonText' | 'confirmButtonText' | 'useCloseAsMainCta' | 'refundModalClass'
>;

export class CancelSubscriptionModal extends React.Component<Props, State> {
  state = {
    didSuccessfullyCancel: false,
    showErrorModal: false,
  };

  handleCancelSubscription = (): Promise<void> => {
    const { subscription, trackConfirm, cancelSubscription } = this.props;
    const _isEligibleForStripeCancellation = isEligibleForStripeCancellation(subscription);
    generateImpressionForCancellationUndo(_isEligibleForStripeCancellation);
    trackConfirm();
    if (_isEligibleForStripeCancellation && isCancellationUndoEnabled() && cancelSubscription) {
      return cancelSubscription(subscription.id)
        .then(() => {
          this.setState(() => ({ didSuccessfullyCancel: true }));
        })
        .catch(() => {
          this.setState(() => ({ showErrorModal: true }));
        });
    }

    return cancel(subscription.id)
      .then(() => {
        this.setState(() => ({ didSuccessfullyCancel: true }));
      })
      .catch(() => {
        this.setState(() => ({ showErrorModal: true }));
      });
  };

  handleClose = () => {
    const { onClose, trackDecline } = this.props;
    const { didSuccessfullyCancel } = this.state;

    onClose(didSuccessfullyCancel);

    if (!didSuccessfullyCancel) {
      trackDecline();
    }
  };

  handleError = () => {
    this.setState(() => ({ showErrorModal: true }));
  };

  getDiscountDisplay(): React.ReactElement | string {
    const { cart, subscriptionNextBillDetails } = this.props;

    if (!cart || !subscriptionNextBillDetails) {
      return '0%';
    }

    // we will assume that cart.originalTotalAmount will never be zero
    const discountAmount = cart.originalTotalAmount - subscriptionNextBillDetails.nextBillingPeriodAmount;
    const discountPercent = Math.trunc((discountAmount / cart.originalTotalAmount) * 100 || 0);

    // subscriptionNextBillDetails doesn't tell us if a flat discount amount or discount percentage is being applied
    // As a quick proxy, we'll only show the raw amount if the discount percent is less than 25% off,
    // or is less than 75% off and not modulus of 5 since setting discount percentages are usually round numbers (e.g. $10 off $59)
    // and otherwise show percentage (e.g. 75%)
    if (discountPercent < 25 || (discountPercent < 75 && discountPercent % 5 !== 0)) {
      const currencyCode = currencyIdMap[subscriptionNextBillDetails.currencyId].code;
      return <ReactPriceDisplay value={discountAmount} currency={currencyCode} hideCurrencyCode={true} />;
    } else {
      return `${discountPercent}%`;
    }
  }

  getTitle(cart?: CartsV2, subscriptionNextBillDetails?: SubscriptionNextBillDetailsV1): React.ReactNode {
    if (isNextBillingCycleDiscounted(cart, subscriptionNextBillDetails)) {
      return (
        <FormattedMessage
          message={_t('Are you sure you don’t want {discountAmount} off?')}
          discountAmount={this.getDiscountDisplay()}
        />
      );
    } else {
      return _t('Are you sure you want to cancel?');
    }
  }

  getAdditionalProps(): AdditionalRefundModalProps {
    const { subscription, cart, subscriptionNextBillDetails } = this.props;
    const isCatalogSubscription = subscription && subscription.isCatalogSubscription;

    if (subscription.isCourseraLiteSubscription || subscription.isCourseraPlusSubscription) {
      return {
        closeButtonText: _t('Keep learning'),
        confirmButtonText: _t('Cancel subscription'),
        useCloseAsMainCta: true,
        refundModalClass: 'rc-RefundModal with-coursera-lite-or-plus-copy',
      };
    } else if (isCatalogSubscription) {
      return {
        closeButtonText: _t('No, continue subscription'),
        confirmButtonText: _t('Yes, cancel subscription'),
        useCloseAsMainCta: true,
      };
    } else if (isNextBillingCycleDiscounted(cart, subscriptionNextBillDetails)) {
      return {
        closeButtonText: _t('Keep Learning'),
        confirmButtonText: _t('Give Up the Discount'),
        useCloseAsMainCta: true,
      };
    } else {
      return {};
    }
  }

  renderCancelSubscription(
    subscription: SubscriptionsV1,
    productName: string,
    cart?: CartsV2,
    subscriptionNextBillDetails?: SubscriptionNextBillDetailsV1
  ): React.ReactNode {
    const subscriptionCancelMessage = getTranslations();
    if (subscription && subscription.nextBillingAt) {
      const { isCatalogSubscription } = subscription;
      let message;
      if (isCatalogSubscription) {
        message = subscriptionCancelMessage.catalogFreeTrial;
        return (
          <FormattedMessage
            message={message}
            productName={productName}
            date={this.renderCancelDate(subscription)}
            tagName="p"
          />
        );
      } else if (isNextBillingCycleDiscounted(cart, subscriptionNextBillDetails)) {
        message = subscriptionCancelMessage.s12nCancelSubscriptionWithDiscount;
        return (
          <div className="discount-message">
            <FormattedMessage
              message={message}
              productName={<strong>{productName}</strong>}
              discount={
                <strong>
                  <FormattedMessage message={_t('{discountAmount} off')} discountAmount={this.getDiscountDisplay()} />
                </strong>
              }
              tagName="p"
            />
          </div>
        );
      } else {
        let messageProps;
        const date = this.renderCancelDate(subscription);
        if (
          subscription.isFreeTrial &&
          (subscription.isCourseraLiteSubscription || subscription.isCourseraPlusSubscription)
        ) {
          message = subscriptionCancelMessage.courseraLiteOrPlusFreeTrial;
          messageProps = { productName };
        } else if (subscription.isFreeTrial) {
          message = subscriptionCancelMessage.s12nFreeTrial;
          messageProps = { productName };
        } else if (subscription.isCourseraLiteSubscription) {
          message = subscriptionCancelMessage.courseraLiteCancelSubscription;
          messageProps = { productName, date };
        } else if (subscription.isCourseraPlusSubscription) {
          const { numberOfProducts } = getCopy();
          message = subscriptionCancelMessage.courseraPlusCancelSubscription;
          messageProps = { productName, numberOfProducts, date };
        } else {
          message = subscriptionCancelMessage.s12nCancelSubscription;
          messageProps = { date };
        }

        const formattedMessage = <FormattedMessage message={message} tagName="p" {...messageProps} />;

        return formattedMessage;
      }
    } else {
      logger.error('Active cancelable subscription must have a next billing date');
      return null;
    }
  }

  renderCancelDate(subscription: SubscriptionsV1) {
    return <FormattedDate value={new Date(subscription.nextBillingAt)} day="numeric" month="long" year="numeric" />;
  }

  render() {
    const { subscription, s12n, cart, subscriptionNextBillDetails } = this.props;
    const { showErrorModal } = this.state;
    let productName;

    if (s12n) {
      productName = getS12nPaymentsDisplayName(s12n?.name, s12n?.productVariant);
    } else if (subscription.isCourseraLiteSubscription) {
      productName = _t('Coursera Lite');
    } else if (subscription.isCourseraPlusSubscription) {
      productName = _t('Coursera Plus');
    }

    const data = subscription && subscription.isFreeTrial ? { isFreeTrial: true } : {};
    const isSpecializationSubscription = subscription && subscription.isSpecializationSubscription;
    const productType = isSpecializationSubscription
      ? SPECIALIZATION_SUBSCRIPTION
      : subscription && subscription.productType;
    const productItemId = subscription && subscription.productItemId;
    const subscriptionId = subscription && subscription.id;
    const subscriptionStartsAt = subscription?.startsAt;

    const description = this.renderCancelSubscription(
      subscription,
      productName || '',
      cart,
      subscriptionNextBillDetails
    );

    const confirmationTitle =
      subscription.isCourseraPlusSubscription || subscription.isCourseraLiteSubscription
        ? _t('Cancellation confirmed')
        : undefined; // use default
    const confirmationDescription =
      subscription.isCourseraPlusSubscription || subscription.isCourseraLiteSubscription ? (
        <FormattedMessage
          message={_t(
            `Your {productName} subscription has been cancelled and you will no longer
           be charged after {date}. You will receive a confirmation email shortly.`
          )}
          date={this.renderCancelDate(subscription)}
          productName={productName}
        />
      ) : (
        _t(
          `Your subscription has been canceled and you'll no longer
         be charged. You will receive a confirmation email shortly.`
        )
      );

    return (
      <div className="rc-CancelSubscriptionModal">
        {showErrorModal ? (
          <RefundErrorModal hasEarnedCertificate={false} handleClose={this.handleClose} />
        ) : (
          <RefundModal
            onClose={this.handleClose}
            onConfirm={this.handleCancelSubscription}
            onError={this.handleError}
            title={this.getTitle(cart, subscriptionNextBillDetails)}
            requestDescription={description}
            confirmationTitle={confirmationTitle}
            confirmationDescription={confirmationDescription}
            modalTrackingName="cancel_subscription_modal"
            closeButtonTrackingName="cancel_subscription_abort"
            confirmButtonTrackingName="cancel_subscription_confirm"
            surveyResponseActionType={SurveyResponseActionType.CANCEL}
            productType={productType}
            productItemId={productItemId}
            subscriptionId={subscriptionId}
            subscriptionStartsAt={subscriptionStartsAt}
            isFreeTrial={subscription.isFreeTrial}
            subscriptionNextBillDetails={this.props.subscriptionNextBillDetails}
            trackingData={data}
            eventingModalName={EVENTING_V3_MODAL_NAME}
            {...this.getAdditionalProps()}
          />
        )}
      </div>
    );
  }
}

const CancelSubscriptionModalContainer = (props: InputProps) => {
  const { subscription } = props;
  const {
    id: subscriptionId,
    cartId,
    isSpecializationSubscription,
    isCourseraPlusSubscription,
    isCourseraLiteSubscription,
    isAppleInApp,
    hasSubscriptionNextBillDetails,
  } = subscription || {};
  const skipSubscriptionNextBillDetails = Boolean(!hasSubscriptionNextBillDetails || isAppleInApp);
  const skipGetCart = !(isSpecializationSubscription || isCourseraPlusSubscription || isCourseraLiteSubscription);

  const { loading, s12n } = useGetSpecializationById({
    s12nId: subscription.productItemId,
    skip: !isSpecializationSubscription,
  });
  const { subscriptionNextBillDetails } = useGetSubscriptionNextBillDetails({
    subscriptionId,
    skip: skipSubscriptionNextBillDetails,
  });
  const { cart } = useCart({ cartId, skip: skipGetCart });

  const track = useTracker();

  const { cancelSubscription } = useCancelSubscription();

  if (subscription && !isSpecializationSubscription && !isCourseraPlusSubscription && !isCourseraLiteSubscription) {
    logger.error(`Unrecognized subscription product type ${subscription.productType}`);
  }

  const trackDecline = () =>
    track('click_button', {
      button: {
        name: 'decline_cancellation',
      },
      modal: {
        name: EVENTING_V3_MODAL_NAME,
      },
      product: getEventingV3Product(subscription),
    });

  const trackConfirm = () =>
    track('click_button', {
      button: {
        name: 'confirm_cancellation',
      },
      modal: {
        name: EVENTING_V3_MODAL_NAME,
      },
      product: getEventingV3Product(subscription),
    });

  if (loading) {
    return null;
  }

  return (
    <CancelSubscriptionModal
      {...props}
      s12n={s12n}
      cart={cart}
      subscriptionNextBillDetails={subscriptionNextBillDetails}
      trackDecline={trackDecline}
      trackConfirm={trackConfirm}
      cancelSubscription={isCancellationUndoEnabled() ? cancelSubscription : undefined}
    />
  );
};

export default CancelSubscriptionModalContainer;
